import { useEffect, useState } from "react";
import { Link } from "wouter";
import { apiFetch } from "../lib/api.js";
import FloorEditor from "../components/FloorEditor.jsx";
import SpineTip from "../components/SpineTip.jsx";
import { IllustrationCompass } from "../components/illustrations.jsx";

// The floor one-liner - canon/11 section 10.1, verbatim.
const FLOOR_TIP = "Your floor: the minimum that still counts as holding the line in a bad week.";

function groundSlug(domain) {
  return domain.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function Ground() {
  const [content, setContent] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([apiFetch("/content/diagnostic"), apiFetch("/dashboard")])
      .then(([c, d]) => {
        setContent(c);
        setDashboard(d);
      })
      .catch((e) => setError(e.message));
  }, []);

  // "Tap one -> its goals" (canon/11 section 4): a ground tile on Home
  // links here with a #ground-<slug> hash; scroll that ground into view
  // once the data has rendered.
  useEffect(() => {
    if (!content || !dashboard) return;
    function scrollToHash() {
      const hash = window.location.hash;
      if (!hash) return;
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ block: "start" });
    }
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, [content, dashboard]);

  async function handleDismissTip(tipId) {
    setDashboard((prev) => (prev ? { ...prev, dismissedTips: [...(prev.dismissedTips || []), tipId] } : prev));
    try {
      await apiFetch("/dashboard/dismiss-tip", { method: "POST", body: { tipId } });
    } catch (e) {
      /* non-critical - worst case the tip reappears next load */
    }
  }

  if (error) return <div className="page">{error}</div>;
  if (!content || !dashboard) return null;

  const dismissedTips = dashboard.dismissedTips || [];

  const bandsByDomain = new Map((dashboard.domainBands || []).map((d) => [d.domain, d]));
  const hasStandingCheck = (dashboard.domainBands || []).length > 0;

  const goalsByDomain = new Map();
  (dashboard.quarterlyGoals || []).forEach((g) => {
    const status = (g.status || "").toLowerCase();
    if (status.includes("done") || status.includes("dropped")) return;
    const domain = content.domains.find((d) => d.toLowerCase() === (g.area || "").toLowerCase());
    if (!domain) return;
    const list = goalsByDomain.get(domain) || [];
    if (list.length < 3) {
      list.push(g);
      goalsByDomain.set(domain, list);
    }
  });

  return (
    <div className="page-wide">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        YOUR GROUND
      </div>
      <h1 style={{ marginBottom: 24 }}>The ground you stand on</h1>

      <div className="illustration-hero-wrap">
        <IllustrationCompass className="illustration-hero" />
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div className="card-title">Your six grounds</div>
          <Link href="/diagnostic" className="btn-secondary" style={{ textDecoration: "none" }}>
            Retake the Standing Check
          </Link>
        </div>
        {!hasStandingCheck && (
          <p className="empty-state">
            The Standing Check scores each ground. Five minutes - take it and the map fills in.
          </p>
        )}
        {hasStandingCheck && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {content.domains.map((domain) => {
              const band = bandsByDomain.get(domain);
              const goals = goalsByDomain.get(domain) || [];
              return (
                <div key={domain} id={`ground-${groundSlug(domain)}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span>{domain}</span>
                    {band ? (
                      <span className={`band-label band-${band.band}`}>{band.band} - {band.percent}%</span>
                    ) : (
                      <span className="empty-state">Not yet measured</span>
                    )}
                  </div>
                  <div className="meter">
                    <div className={`meter-fill band-${band?.band || ""}`} style={{ width: `${band?.percent || 0}%` }} />
                  </div>
                  {goals.length > 0 && (
                    <div className="hairline-list" style={{ marginTop: 10 }}>
                      {goals.map((g, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <span>{g.goal}</span>
                          {g.status && <span className="mono" style={{ color: "var(--muted)" }}>{g.status}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card-grid two-col" style={{ marginBottom: 24 }}>
        <div>
          <div className="card-title" style={{ marginBottom: 16 }}>Vision sketch</div>
          {dashboard.visionSketch ? (
            <p style={{ lineHeight: 1.6 }}>{dashboard.visionSketch}</p>
          ) : (
            <p className="empty-state">No vision sketch yet. Your corner builds it with you - about an hour.</p>
          )}
        </div>
        <div>
          <div className="card-title" style={{ marginBottom: 16 }}>Quarterly goals</div>
          {dashboard.quarterlyGoals && dashboard.quarterlyGoals.length > 0 ? (
            <div className="hairline-list">
              {dashboard.quarterlyGoals.map((g, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{g.goal}{g.area ? ` · ${g.area}` : ""}</span>
                  {g.status && <span className="mono" style={{ color: "var(--muted)" }}>{g.status}</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No goals yet. They come out of your vision.</p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 8 }}>Your floor</div>
        <SpineTip
          tipId="the-floor"
          text={FLOOR_TIP}
          dismissedTips={dismissedTips}
          onDismiss={handleDismissTip}
        />
        <FloorEditor />
      </div>
    </div>
  );
}
